import { useState, useMemo, useCallback, useEffect } from "react";
import { useCities, useDeleteCity } from "@/hooks/useCities";
import { useCountries } from "@/hooks/useCountries";
import { CityDialog } from "@/components/cities/CityDialog";
import { CitiesTable } from "@/components/cities/CitiesTable";
import { CitiesFilters } from "@/components/cities/CitiesFilters";
import { SmartButton } from "@/components/ui/interactive/SmartButton";
import { RealTimeUpdater } from "@/components/ui/interactive/RealTimeUpdater";
import { AdvancedSearch, SearchConfig } from "@/components/ui/search-advanced";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { DataExport } from "@/components/ui/data-export";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { DynamicCard } from "@/components/ui/interactive/DynamicCard";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, RefreshCw, Edit, Trash2, Globe, MapPin } from "lucide-react";
const Cities = () => {
  const {
    data: cities = [],
    isLoading: citiesLoading,
    refetch
  } = useCities();
  const {
    data: countries = [],
    isLoading: countriesLoading
  } = useCountries();
  const deleteCity = useDeleteCity();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Real-time notifications
  useEffect(() => {
    const channel = supabase.channel('cities_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'cities'
    }, payload => {
      console.log('City change detected:', payload);
      refetch();
      switch (payload.eventType) {
        case 'INSERT':
          toast.success('تم إضافة مدينة جديدة');
          break;
        case 'UPDATE':
          toast.info('تم تحديث بيانات مدينة');
          break;
        case 'DELETE':
          toast.warning('تم حذف مدينة');
          break;
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Advanced search config
  const searchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في المدن...",
    filters: [{
      id: 'country',
      label: 'الدولة',
      type: 'select',
      options: countries.map(country => ({
        value: country.id,
        label: country.name_ar
      })),
      icon: Globe
    }]
  }), [countries]);
  const filteredCities = useMemo(() => {
    return cities.filter(city => {
      const matchesSearch = city.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) || city.name_en && city.name_en.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCountry = selectedCountry === "all" || city.country_id === selectedCountry;
      return matchesSearch && matchesCountry;
    });
  }, [cities, searchTerm, selectedCountry]);

  // Table columns
  const tableColumns: TableColumn<any>[] = useMemo(() => [{
    key: 'name_ar',
    title: 'اسم المدينة',
    sortable: true,
    render: (_, record) => <div className="font-medium text-card-foreground">{record.name_ar}</div>
  }, {
    key: 'name_en',
    title: 'الاسم بالإنجليزية',
    sortable: true,
    render: (_, record) => record.name_en || '-'
  }, {
    key: 'country_id',
    title: 'الدولة',
    render: (_, record) => {
      const country = countries.find(c => c.id === record.country_id);
      return country ? <Badge variant="secondary">{country.name_ar}</Badge> : '-';
    }
  }, {
    key: 'actions',
    title: 'الإجراءات',
    align: 'center',
    render: (_, record) => <div className="flex gap-2 justify-center">
          <SmartButton variant="outline" size="sm" onClick={() => handleEdit(record)}>
            تعديل
          </SmartButton>
          <SmartButton variant="destructive" size="sm" onClick={() => handleDelete(record.id)} requireConfirmation confirmationTitle="حذف المدينة" confirmationDescription={`هل أنت متأكد من حذف مدينة ${record.name_ar}؟`} successMessage="تم حذف المدينة" showSuccessState>
            حذف
          </SmartButton>
        </div>
  }], [countries]);
  const [selectedCity, setSelectedCity] = useState<any>(null);
  const handleEdit = useCallback((city: any) => {
    setSelectedCity(city);
    setDialogOpen(true);
  }, []);
  const handleDelete = useCallback(async (cityId: string) => {
    try {
      await deleteCity.mutateAsync(cityId);
      toast.success('تم حذف المدينة بنجاح');
    } catch (error) {
      toast.error('حدث خطأ أثناء حذف المدينة');
    }
  }, [deleteCity]);
  const handleSearch = useCallback((query: string, filters: any[]) => {
    setSearchTerm(query);
    const countryFilter = filters.find(f => f.id === 'country');
    if (countryFilter) {
      setSelectedCountry(countryFilter.value);
    }
  }, []);
  const handleManualRefresh = useCallback(async () => {
    toast.info("جاري تحديث البيانات...");
    try {
      await refetch();
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البيانات");
    }
  }, [refetch]);
  if (citiesLoading || countriesLoading) {
    return <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل المدن..." />
      </div>;
  }
  return <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <RealTimeUpdater tableName="cities" onUpdate={() => refetch()} showStatus={true}>
        {/* الهيدر */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-card-foreground">إدارة المدن</h1>
            
            
          </div>
          
          <div className="flex gap-2 items-center">
            <ViewToggle view={view} onViewChange={setView} />
            
            <DataExport data={filteredCities} columns={[{
            key: 'name_ar',
            title: 'الاسم بالعربية',
            dataIndex: 'name_ar'
          }, {
            key: 'name_en',
            title: 'الاسم بالإنجليزية',
            dataIndex: 'name_en'
          }, {
            key: 'country',
            title: 'الدولة',
            dataIndex: 'country_id'
          }]} filename="cities" formats={['csv', 'json']} />
            
            <SmartButton onClick={handleManualRefresh} variant="outline" size="sm" icon={RefreshCw}>
              تحديث
            </SmartButton>
            
            <SmartButton onClick={() => setDialogOpen(true)} icon={Plus} successMessage="تم إضافة المدينة" showSuccessState>
              إضافة مدينة
            </SmartButton>
          </div>
        </div>

        {/* البحث المتقدم */}
        <AdvancedSearch config={searchConfig} onSearch={handleSearch} initialQuery={searchTerm} />

        {/* المحتوى */}
        {view === 'cards' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCities.map(city => {
          const country = countries.find(c => c.id === city.country_id);
          return <DynamicCard key={city.id} title={city.name_ar} description={city.name_en} badge={country ? {
            text: country.name_ar,
            variant: "secondary"
          } : undefined} hoverEffect="lift" pulseOnUpdate onEdit={() => handleEdit(city)} onDelete={() => handleDelete(city.id)}>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{country?.name_ar || 'غير محدد'}</span>
                  </div>
                </DynamicCard>;
        })}
          </div> : <OptimizedTable columns={tableColumns} data={filteredCities} loading={citiesLoading} pagination={{
        current: 1,
        pageSize: 20,
        total: filteredCities.length,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100]
      }} rowKey="id" emptyText="لا توجد مدن" size="middle" />}
        
        {filteredCities.length === 0 && !citiesLoading && <div className="text-center py-12 text-muted-foreground">
            <p>لم يتم العثور على أي مدن.</p>
          </div>}

        <CityDialog city={selectedCity} trigger={null} onSuccess={() => {
        refetch();
        setDialogOpen(false);
        setSelectedCity(null);
      }} open={dialogOpen} onOpenChange={setDialogOpen} />
      </RealTimeUpdater>
    </div>;
};
export default Cities;